/**
 * Centralized user-friendly error messages.
 * Maps technical errors to plain-language messages with actionable suggestions.
 */

const ERROR_PATTERNS = [
  // Browser/Extension blocking
  {
    match: (msg) => /ERR_BLOCKED_BY_CLIENT|blocked by client/i.test(msg),
    friendly: 'Your browser or an ad blocker is preventing this action. Please disable the ad blocker for this site and try again.',
  },
  // Pop-up blocked
  {
    match: (msg) => /popup.*blocked|blocked.*popup/i.test(msg),
    friendly: 'A pop-up was blocked by your browser. Please allow pop-ups for this site and try again.',
  },
  // Network / connectivity
  {
    match: (msg) => /Failed to fetch|NetworkError|ERR_INTERNET|ERR_CONNECTION|ERR_NAME_NOT_RESOLVED|net::ERR_/i.test(msg),
    friendly: 'Unable to reach the server. Please check your internet connection and try again.',
  },
  // Cloudflare / proxy errors
  {
    match: (msg) => /520|521|522|523|524/i.test(msg),
    friendly: 'The server is temporarily busy. Please wait a moment and try again.',
  },
  {
    match: (msg) => /502|bad gateway/i.test(msg),
    friendly: 'The server is restarting. Please wait a moment and try again.',
  },
  {
    match: (msg) => /503|service unavailable/i.test(msg),
    friendly: 'The service is temporarily unavailable. Please try again in a few minutes.',
  },
  {
    match: (msg) => /504|gateway timeout/i.test(msg),
    friendly: 'The request took too long to process. Please try again.',
  },
  // 405 Method Not Allowed
  {
    match: (msg) => /405|method not allowed/i.test(msg),
    friendly: 'This action is not supported. Please refresh the page and try again.',
  },
  // 404 Not Found
  {
    match: (msg) => /404|not found/i.test(msg),
    friendly: 'The requested item could not be found. It may have been moved or deleted.',
  },
  // 422 Validation Error
  {
    match: (msg) => /422|validation error|unprocessable/i.test(msg),
    friendly: 'Some of the information provided is incorrect. Please check your inputs and try again.',
  },
  // 429 Rate Limit
  {
    match: (msg) => /429|too many requests|rate limit/i.test(msg),
    friendly: 'Too many requests. Please wait a moment before trying again.',
  },
  // 403 / forbidden (often WAF)
  // iter-230al — A bare "Not authenticated" 403 means the request
  // reached the backend without an Authorization header (most often
  // a fresh-signup race before sessionStorage was populated). Map
  // that to the session-expired copy instead of the ad-blocker /
  // VPN message which mis-blamed the user.
  {
    match: (msg) => /403/i.test(msg) && /not authenticated|missing.*token|missing.*auth/i.test(msg),
    friendly: 'Your session needs to refresh. Please try again in a moment, or log in again to continue.',
  },
  {
    match: (msg) => /403|forbidden/i.test(msg),
    friendly: 'This action was blocked. If you have an ad blocker or VPN enabled, try disabling it and retry.',
  },
  // Auth / session expired
  {
    match: (msg) => /401|unauthorized|token.*expired|session.*expired|not authenticated/i.test(msg),
    friendly: 'Your session has expired. Please log in again to continue.',
  },
  // File upload issues
  {
    match: (msg) => /file.*too.*large|payload.*too.*large|413/i.test(msg),
    friendly: 'The file is too large to upload. Please try a smaller file (under 10MB).',
  },
  // Timeout
  {
    match: (msg) => /timeout|timed out|408/i.test(msg),
    friendly: 'The request took too long. Please try again.',
  },
  // JSON parse error
  {
    match: (msg) => /JSON\.parse|Unexpected token|JSON|syntax error/i.test(msg),
    friendly: 'Something went wrong while processing the response. Please refresh and try again.',
  },
  // CORS
  {
    match: (msg) => /CORS|cross.origin|access.control/i.test(msg),
    friendly: 'A connection issue occurred. Please refresh the page and try again.',
  },
  // Internal server error
  {
    match: (msg) => /500|internal server error/i.test(msg),
    friendly: 'Something went wrong on our end. Please try again. If the problem persists, contact support.',
  },
  // HTTP generic
  {
    match: (msg) => /^HTTP \d{3}/i.test(msg),
    friendly: 'Something went wrong. Please try again. If the problem persists, refresh the page.',
  },
];

/**
 * Convert a technical error into a user-friendly message.
 * @param {Error|string} error - The error object or message string
 * @param {string} fallback - Optional custom fallback message
 * @returns {string} User-friendly error message
 */
export function getFriendlyError(error, fallback) {
  const msg = typeof error === 'string' ? error : (error?.message || error?.toString?.() || '');

  for (const pattern of ERROR_PATTERNS) {
    if (pattern.match(msg)) {
      return pattern.friendly;
    }
  }

  // iter-230aq — If the backend already returned a clear, human-readable
  // message (e.g. "This PAN number is already registered with another
  // vendor account..."), pass it through verbatim instead of clobbering
  // it with a generic fallback. Heuristic:
  //   • Reasonable length (10-300 chars)
  //   • Contains at least one space (i.e. an English sentence, not a code)
  //   • Doesn't look like a technical token (no stack-trace / HTTP prefix
  //     / file-path markers — those were already handled above).
  // This is what surfaces the actionable "PAN already registered",
  // "Store name taken", "GSTIN already linked" copy that the backend
  // already crafts for the user.
  const trimmed = (msg || '').trim();
  const looksTechnical =
    /^https?:\/\//i.test(trimmed) ||
    /\bat [A-Za-z_$][\w$]*\s*\(/i.test(trimmed) ||      // stack frame
    /\.\.\.\s*$|^\{|^\[/i.test(trimmed) ||                // JSON dump
    /^HTTP\s*\d{3}\b/i.test(trimmed) ||
    /undefined|null|NaN|TypeError|ReferenceError/i.test(trimmed);
  if (
    trimmed.length >= 10 &&
    trimmed.length <= 300 &&
    /\s/.test(trimmed) &&
    !looksTechnical
  ) {
    return trimmed;
  }

  return fallback || 'Something went wrong. Please try again. If the issue persists, refresh the page or contact support.';
}

/**
 * Wrap an API error for toast display.
 * @param {Error|string} error
 * @param {string} context - What the user was trying to do (e.g. "connect to Zoho")
 * @returns {{ title: string, description: string }}
 */
export function friendlyToast(error, context) {
  const friendly = getFriendlyError(error);
  return {
    title: context ? `Unable to ${context}` : 'Something went wrong',
    description: friendly,
  };
}
